/**
 * Headless deterministic arena match (Arena §11, Movement §10). Ties the map + nav +
 * bot AI + perception + combat + gold + hazards + shrink into one loop and runs it to
 * a result. Same logic the live match renders; this is the version the difficulty
 * harness runs 1000× to verify the win-rate targets.
 *
 * Movement here is a lean greedy local mover (step toward the AI's intent, slide/greedy
 * around blockers, descend distToSafe to retreat) rather than the full steering + A*
 * queue — the harness needs speed and only the *outcome* (placement, kills, gold)
 * feeds scoring. The live view uses the full nav stack for feel.
 */

import { mulberry32 } from '../map/prng';
import { generateArena, inSafeSpace, type ArenaMap } from './map';
import { buildArenaNav, type ArenaNav } from '../nav/arena-source';
import { tileIndex } from '../nav/grid';
import { generateRoster, rollTier, type Personality, type SkillTier } from './names';
import { deriveProfile, type BotProfile } from './profile';
import { decide, newBotMemory, type AiWorld, type BotMemory, type Intent } from './bot-ai';
import { buildEnemyViews, updateFacing, type SenseEnemy } from './bot-sense';
import { ARENA, killGold, gearedDamage, rollBotGear, rollHit, type ArenaGear } from './combat';
import { Tile } from './map';
import { compareSurvivors, type HeroResult } from './scoring';

interface Agent {
  id: string; profile: BotProfile; gear: ArenaGear;
  x: number; y: number;
  hp: number; maxHp: number; alive: boolean;
  pendingGold: number; goldBanked: number; streak: number;
  kills: number; killedBy: string | null;
  safeCdMs: number; attackCdMs: number; campMs: number;
  timeAliveMs: number; entryOrder: number; deathOrder: number;
  rng: () => number;
  /** Perception memory, target commitment, facing, decision stagger — see bot-ai.ts. */
  mem: BotMemory;
  /** Previous position, for the facing heading and the "was it moving?" aim penalty. */
  px: number; py: number;
  moving: boolean;
  wasInSafe: boolean;
  isPlayer: boolean;
}

const PERSONALITIES: Personality[] = ['aggressor', 'farmer', 'camper', 'tactician', 'coward'];
const SIM_DT_MS = 200;         // 5 Hz — movement + combat step
/**
 * How often a bot re-DECIDES, as opposed to how often the world steps. Matches the
 * live match's AI_TICK_MS. Between decisions the last intent is replayed, so a bot
 * keeps walking where it was going instead of re-planning five times a second.
 */
const AI_DECIDE_MS = 200;
const HERO_COUNT = 25;

export interface SimResult {
  results: HeroResult[];
  /** Placement (1 = winner) of the reference "player", if one was inserted. */
  playerPlace: number | null;
  durationMs: number;
}

export interface SimOptions {
  /** Insert a reference "average player" bot (regular/farmer) to measure win rate. */
  withPlayer?: boolean;
  maxMs?: number;
}

export function simulateMatch(seed: number, opts: SimOptions = {}): SimResult {
  const map: ArenaMap = generateArena(seed);
  const nav: ArenaNav = buildArenaNav(map);
  const rng = mulberry32(seed ^ 0x51ed270b);
  const roster = generateRoster(seed, HERO_COUNT);

  // Scatter a spawn across the safe space — NOT the centre. (Sampling radius from 0
  // stacked all 25 agents on the exact centre tile, which then all left in the same
  // direction and the first-mover walked into a waiting camper every match.)
  const walkableNear = (cx: number, cy: number): [number, number] => {
    for (let attempt = 0; attempt < 40; attempt++) {
      const a = rng() * Math.PI * 2, rr = 1 + rng() * (map.safe.r - 1.5);
      const tx = Math.floor(cx + Math.cos(a) * rr), ty = Math.floor(cy + Math.sin(a) * rr);
      if (tx >= 0 && ty >= 0 && tx < map.w && ty < map.h && nav.grid.walkable[tileIndex(tx, ty, map.w)]) return [tx + 0.5, ty + 0.5];
    }
    return [map.safe.cx + 0.5, map.safe.cy + 0.5];
  };

  const agents: Agent[] = roster.map((b, i) => {
    // The reference "average player" is just a normal competitor drawn from the same
    // field distribution (index 0). Measuring its win rate tests whether the field as
    // a whole is beatable ~1/25 by an average entrant.
    const tier: SkillTier = rollTier(rng);
    const pers: Personality = PERSONALITIES[Math.floor(rng() * PERSONALITIES.length)];
    const [x, y] = walkableNear(map.safe.cx, map.safe.cy);
    const profile = deriveProfile(tier, pers, rng);
    return {
      id: b.id, profile,
      // Crafted gear counts in the arena, so the harness has to measure a field
      // that WEARS some — a sim where nobody is geared would report a top-1 rate
      // for a game that no longer exists.
      gear: rollBotGear(rng),
      x, y, hp: 100, maxHp: 100, alive: true,
      pendingGold: 0, goldBanked: 0, streak: 0, kills: 0, killedBy: null,
      // Staggered exit: bots leave over the first ~15–55s rather than pouring out in
      // one synchronised bloodbath, so fights are distributed 1v1/small, not a scrum.
      safeCdMs: ARENA.SAFE_COOLDOWN_MS + Math.floor(rng() * 40_000), attackCdMs: 0, campMs: 0,
      timeAliveMs: 0, entryOrder: i, deathOrder: 0,
      rng: mulberry32(((seed * 2654435761) ^ ((i + 1) * 40503)) >>> 0),
      // Decision stagger. Every agent used to decide on EVERY step; the live match has
      // staggered `aiCd` and the harness did not, so the two were not even measuring
      // the same tick rate. Bounded per-frame work is the charter's stability clause.
      mem: newBotMemory(profile.reactionMs, Math.floor(rng() * AI_DECIDE_MS)),
      px: x, py: y, moving: false,
      wasInSafe: false, isPlayer: !!opts.withPlayer && i === 0,
    };
  });

  const distToSafe = (x: number, y: number) => {
    const tx = Math.floor(x), ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h) return Infinity;
    return nav.distToSafe[tileIndex(tx, ty, map.w)];
  };
  const blocked = (x: number, y: number) => {
    const tx = Math.floor(x), ty = Math.floor(y);
    return tx < 0 || ty < 0 || tx >= map.w || ty >= map.h || nav.grid.walkable[tileIndex(tx, ty, map.w)] === 0;
  };
  const nearestChoke = (x: number, y: number) => {
    let best: { x: number; y: number } | null = null, bd = Infinity;
    for (const c of nav.chokepoints) {
      const cx = c % map.w, cy = (c - (c % map.w)) / map.w;
      const d = Math.hypot(cx - x, cy - y);
      if (d < bd) { bd = d; best = { x: cx + 0.5, y: cy + 0.5 }; }
    }
    return best;
  };

  // Line-of-sight / shroom blockers, in TILE space, for the perception layer.
  const blocksTile = (tx: number, ty: number) =>
    tx < 0 || ty < 0 || tx >= map.w || ty >= map.h || nav.grid.walkable[tileIndex(tx, ty, map.w)] === 0;
  const shroomTile = (tx: number, ty: number) =>
    tx >= 0 && ty >= 0 && tx < map.w && ty < map.h && map.tiles[tileIndex(tx, ty, map.w)] === Tile.Shroom;

  /**
   * Geysers, at last. `map.geysers` has been generated every match since the arena
   * was written, the GDD specifies a 1.5 s telegraph and 25 damage, bot-ai.ts has
   * always had a dodge reflex as its very first branch — and nothing ever armed one,
   * because both callers hard-coded `armedHazard: null`. They erupt now, on a
   * per-geyser phase so the map does not pulse in unison, and a bot that sees the
   * telegraph under itself jumps clear.
   */
  const gPhase = map.geysers.map((_, i) => (i * 4133 + seed * 97) % ARENA.GEYSER_PERIOD_MS);
  const geyserArmed = (t: number, i: number) => {
    const ph = (t + gPhase[i]) % ARENA.GEYSER_PERIOD_MS;
    return ph >= ARENA.GEYSER_PERIOD_MS - ARENA.GEYSER_TELEGRAPH_MS;
  };
  const geyserErupts = (t: number, i: number) => {
    const a = (t + gPhase[i]) % ARENA.GEYSER_PERIOD_MS;
    const b = (t + SIM_DT_MS + gPhase[i]) % ARENA.GEYSER_PERIOD_MS;
    return b < a;   // the phase wrapped inside this step
  };
  const GEYSER_R = 1.8, GEYSER_WATCH_R = 4;
  const armedNear = (t: number, x: number, y: number): { x: number; y: number } | null => {
    for (let i = 0; i < map.geysers.length; i++) {
      if (!geyserArmed(t, i)) continue;
      const g = map.geysers[i];
      if (Math.hypot(g.tx + 0.5 - x, g.ty + 0.5 - y) <= GEYSER_WATCH_R) return { x: g.tx + 0.5, y: g.ty + 0.5 };
    }
    return null;
  };

  const maxMs = opts.maxMs ?? ARENA.BATTLE_MS;
  let t = 0, deaths = 0, step = 0;
  const alive = () => agents.filter((a) => a.alive);

  while (t < maxMs && alive().length > 1) {
    // Rotate who acts first each step — otherwise agent 0 always moves before the
    // rest react to it, a permanent positional disadvantage.
    const rot = step++ % agents.length;
    const shrinking = t > maxMs - ARENA.SHRINK_LAST_MS;
    // Shrink radius: everything beyond this from centre becomes lethal in the last 2 min.
    const shrinkR = shrinking ? map.w * 0.5 * (1 - 0.6 * ((t - (maxMs - ARENA.SHRINK_LAST_MS)) / ARENA.SHRINK_LAST_MS)) : Infinity;

    for (let k = 0; k < agents.length; k++) {
      const a = agents[(k + rot) % agents.length];
      if (!a.alive) continue;
      a.timeAliveMs += SIM_DT_MS;
      a.safeCdMs = Math.max(0, a.safeCdMs - SIM_DT_MS);
      a.attackCdMs = Math.max(0, a.attackCdMs - SIM_DT_MS);
      const battleRemain = maxMs - t;
      const safeOpen = battleRemain > ARENA.SAFE_CLOSE_LAST_MS;   // closes 3 min before end
      let inSafe = safeOpen && inSafeSpace(map, a.x, a.y);
      if (inSafe) {
        a.campMs += SIM_DT_MS;
        if (a.campMs > ARENA.SAFE_CAMP_MAX_MS) {   // camped >30s → kicked to the perimeter
          const dx = a.x - map.safe.cx, dy = a.y - map.safe.cy, l = Math.hypot(dx, dy) || 1;
          a.x = map.safe.cx + (dx / l) * (map.safe.r + 1.5); a.y = map.safe.cy + (dy / l) * (map.safe.r + 1.5);
          a.campMs = 0; inSafe = false;
        } else {
          // No healing here (see ARENA in combat.ts) — the sanctuary banks gold and shields.
          if (!a.wasInSafe) {
            if (a.pendingGold > 0) { a.goldBanked += Math.floor(a.pendingGold); a.pendingGold = 0; a.streak = 0; }
            a.safeCdMs = ARENA.SAFE_COOLDOWN_MS;
          }
        }
      } else a.campMs = 0;
      a.wasInSafe = inSafe;

      // Decide on a stagger, not every step, and re-use the last intent in between —
      // this is what keeps the per-frame cost bounded with 25 fighters on the map.
      a.mem.aiCdMs -= SIM_DT_MS;
      let intent: Intent = a.mem.last ?? { kind: 'idle' };
      if (a.mem.aiCdMs <= 0) {
        // Per-decision latency: reactionMs is a match constant, the jitter is re-rolled,
        // so a bot's responses are uneven the way a person's are.
        a.mem.aiCdMs = AI_DECIDE_MS + Math.floor(a.rng() * a.profile.decisionJitterMs);
        a.mem.facing = updateFacing(a.mem.facing, a.x - a.px, a.y - a.py);

        // Perception. NOT "every agent within 18 tiles": vision cone + supercover
        // line of sight + shroom clouds + reaction latency + 4 s memory, exactly what
        // the player's own eyes get. See arena/bot-sense.ts.
        const visible: SenseEnemy[] = [];
        for (const e of agents) {
          if (e === a || !e.alive) continue;
          visible.push({ id: e.id, x: e.x, y: e.y, hpPct: e.hp / e.maxHp, kills: e.kills });
        }
        const seen = buildEnemyViews({
          selfId: a.id, x: a.x, y: a.y, facing: a.mem.facing, nowMs: t,
          perception: a.mem.perception, facts: a.mem.facts,
          blocks: blocksTile, shroomAt: shroomTile,
        }, visible);

        const world: AiWorld = {
          self: { id: a.id, x: a.x, y: a.y, hp: a.hp, maxHp: a.maxHp, pendingGold: a.pendingGold, profile: a.profile, safeCooldownMs: a.safeCdMs, inSafe },
          enemies: seen,
          safeCenter: { x: map.safe.cx + 0.5, y: map.safe.cy + 0.5 },
          distToSafe, nearestChokepoint: nearestChoke, danger: () => 0,
          armedHazard: armedNear(t, a.x, a.y),
          // The sim's ring really does kill (see the lethal check below), so bots are
          // right to run from it here — unlike the live match, which has no ring damage.
          shrinking, ringLethal: true, attackRange: a.profile.range, aliveCount: alive().length,
          nowMs: t, bounds: { w: map.w, h: map.h },
          memory: a.mem, rng: a.rng,
        };
        intent = decide(world);
        a.mem.last = intent;
      }
      a.px = a.x; a.py = a.y;

      if (intent.kind === 'attack') {
        const target = agents.find((x2) => x2.id === intent.targetId);
        // No damage inside the safe space (attacker or target) — it's a sanctuary.
        if (target && target.alive && a.attackCdMs <= 0 && !inSafe && !inSafeSpace(map, target.x, target.y)) {
          a.attackCdMs = a.profile.cooldownMs;
          if (rollHit(a.profile, {
            targetMoving: target.moving, selfHpPct: a.hp / a.maxHp,
            dist: Math.hypot(target.x - a.x, target.y - a.y), range: a.profile.range,
          }, a.rng())) {
            target.hp -= gearedDamage(a.profile.damage, a.gear, target.gear);
            if (target.hp <= 0) killAgent(target, a);
          }
        }
      } else if (intent.kind === 'move' || intent.kind === 'dodge') {
        stepToward(a, (intent as { x: number; y: number }).x, (intent as { x: number; y: number }).y);
      }
      a.moving = Math.hypot(a.x - a.px, a.y - a.py) > 0.05;

      // Geyser eruption: 25 damage inside the plume, telegraphed 1.5 s ahead so a bot
      // that reads it can be somewhere else. A player gets the same warning.
      for (let gi = 0; gi < map.geysers.length; gi++) {
        if (!geyserErupts(t, gi)) continue;
        const g = map.geysers[gi];
        if (Math.hypot(g.tx + 0.5 - a.x, g.ty + 0.5 - a.y) <= GEYSER_R) {
          a.hp -= ARENA.GEYSER_DMG;
          if (a.hp <= 0) { killAgent(a, null); break; }
        }
      }

      // Hazard: caught outside the shrinking ring → lethal.
      if (shrinking && Math.hypot(a.x - map.safe.cx, a.y - map.safe.cy) > shrinkR) {
        a.hp -= ARENA.LAVA_DPS * (SIM_DT_MS / 1000) * 4;
        if (a.hp <= 0) killAgent(a, null);
      }
    }
    t += SIM_DT_MS;
  }

  function killAgent(victim: Agent, killer: Agent | null) {
    if (!victim.alive) return;
    victim.alive = false;
    victim.deathOrder = ++deaths;
    victim.killedBy = killer?.id ?? null;
    victim.pendingGold = 0;   // lost on death (banked stays)
    if (killer) {
      killer.pendingGold += killGold(killer.streak);
      killer.streak += 1;
      killer.kills += 1;
    }
  }

  // Greedy local move toward (gx,gy): straight step, else the best walkable neighbour.
  function stepToward(a: Agent, gx: number, gy: number) {
    const speed = 5.0 * (SIM_DT_MS / 1000);   // tiles this step
    const dx = gx - a.x, dy = gy - a.y, d = Math.hypot(dx, dy) || 1;
    let nx = a.x + (dx / d) * speed, ny = a.y + (dy / d) * speed;
    if (!blocked(nx, ny)) { a.x = nx; a.y = ny; return; }
    // Slide / greedy: pick the walkable 8-neighbour tile centre closest to the goal.
    let best: [number, number] | null = null, bd = Infinity;
    for (const [ox, oy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      const cx = Math.floor(a.x) + ox + 0.5, cy = Math.floor(a.y) + oy + 0.5;
      if (blocked(cx, cy)) continue;
      const dd = Math.hypot(gx - cx, gy - cy);
      if (dd < bd) { bd = dd; best = [cx, cy]; }
    }
    if (best) {
      const bx = best[0] - a.x, by = best[1] - a.y, bl = Math.hypot(bx, by) || 1;
      nx = a.x + (bx / bl) * speed; ny = a.y + (by / bl) * speed;
      if (!blocked(nx, ny)) { a.x = nx; a.y = ny; }
    }
  }

  // Build placements. Survivors (alive at time-expiry) rank ahead of the dead, and
  // among themselves by compareSurvivors — the Score formula minus its own place
  // term (scoring.ts). Among the dead, a later death places higher.
  //
  // This sort is DUPLICATED by buildLiveResults in views/arena-match.ts, which is why
  // the ordering lives in scoring.ts: the sim is the fallback the live view falls back
  // to, and the two disagreeing about what 1st place means is defect 16.
  const ranked = [...agents].sort((p, q) => {
    if (p.alive !== q.alive) return p.alive ? -1 : 1;
    if (p.alive) {
      return compareSurvivors(
        { kills: p.kills, goldBanked: p.goldBanked, hpPct: p.hp / p.maxHp, timeAliveMs: p.timeAliveMs, entryOrder: p.entryOrder },
        { kills: q.kills, goldBanked: q.goldBanked, hpPct: q.hp / q.maxHp, timeAliveMs: q.timeAliveMs, entryOrder: q.entryOrder },
      );
    }
    return q.deathOrder - p.deathOrder;
  });
  const results: HeroResult[] = ranked.map((a, i) => ({
    heroId: a.id,
    survivalPlace: i + 1,
    kills: a.kills,
    goldBanked: a.goldBanked,
    survived: a.alive,
    timeAliveMs: a.timeAliveMs,
    entryOrder: a.entryOrder,
  }));

  const playerAgent = agents.find((a) => a.isPlayer);
  const playerPlace = playerAgent ? results.find((r) => r.heroId === playerAgent.id)!.survivalPlace : null;
  return { results, playerPlace, durationMs: t };
}
